import api from '@/lib/api';
import type {
  RetailHouseAccount,
  RetailHouseAccountItem,
  RetailHouseAccountItemDirection,
} from '@pms/shared';

// ─── Query / payload types ────────────────────────────────────────────────────

export interface ListRetailHouseAccountsQuery {
  propertyId: string;
  activeOnly?: boolean | undefined;
}

export interface CreateRetailHouseAccountPayload {
  propertyId: string;
  name: string;
  code: string;
  description?: string | undefined;
  isActive?: boolean | undefined;
}

export interface UpdateRetailHouseAccountPayload {
  name?: string | undefined;
  code?: string | undefined;
  description?: string | undefined;
  isActive?: boolean | undefined;
}

export interface PostRetailHouseAccountItemPayload {
  direction: RetailHouseAccountItemDirection;
  /** Positive amount, >= 0.01. */
  amount: number;
  /** Posts as the ledger `note`. */
  note: string;
  entryDate?: string | undefined;
}

export interface SettleRetailHouseAccountPayload {
  /** Positive amount, capped server-side at the outstanding balance. */
  amount: number;
}

/** GET /retail/house-accounts/:id → header + ledger items. */
export interface RetailHouseAccountDetailResponse {
  account: RetailHouseAccount;
  items: RetailHouseAccountItem[];
}

/** A posted item alongside the server-authoritative running balance. */
export interface RetailHouseAccountItemResult {
  item: RetailHouseAccountItem;
  balance: string;
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function fetchRetailHouseAccounts(
  query: ListRetailHouseAccountsQuery,
): Promise<RetailHouseAccount[]> {
  const { data } = await api.get<RetailHouseAccount[]>('/retail/house-accounts', {
    params: query,
  });
  return data;
}

export async function fetchRetailHouseAccount(
  id: string,
): Promise<RetailHouseAccountDetailResponse> {
  const { data } = await api.get<RetailHouseAccountDetailResponse>(
    `/retail/house-accounts/${id}`,
  );
  return data;
}

export async function createRetailHouseAccount(
  payload: CreateRetailHouseAccountPayload,
): Promise<RetailHouseAccount> {
  const { data } = await api.post<RetailHouseAccount>('/retail/house-accounts', payload);
  return data;
}

export async function updateRetailHouseAccount(
  id: string,
  payload: UpdateRetailHouseAccountPayload,
): Promise<RetailHouseAccount> {
  const { data } = await api.patch<RetailHouseAccount>(
    `/retail/house-accounts/${id}`,
    payload,
  );
  return data;
}

export async function postRetailHouseAccountItem(
  id: string,
  payload: PostRetailHouseAccountItemPayload,
): Promise<RetailHouseAccountItemResult> {
  const { data } = await api.post<RetailHouseAccountItemResult>(
    `/retail/house-accounts/${id}/items`,
    payload,
  );
  return data;
}

/** Settle (pay off) part/all of an account balance. 400 OVER_SETTLE if too large. */
export async function settleRetailHouseAccount(
  id: string,
  payload: SettleRetailHouseAccountPayload,
): Promise<RetailHouseAccountItemResult> {
  const { data } = await api.post<RetailHouseAccountItemResult>(
    `/retail/house-accounts/${id}/settle`,
    payload,
  );
  return data;
}
